/**
 * Whole-array variable injection for primitive array fields of swagger request bodies.
 *
 * A normal array field stores one JSON token list per entry, e.g. `["[{\"name\":\"a\",...}]", ...]`.
 * Injecting a variable replaces the entries with a single token list that references the variable
 * and carries {@link ARRAY_INJECTION_MARKER}, so the gateway sends the referenced JSON array as
 * the whole field instead of one string entry.
 */

/** Provided by the request body view when the connector compiles array injections. */
export const ARRAY_INJECTION_ENABLED_KEY = Symbol("arrayInjectionEnabled");

/**
 * Must match `SwaggerRequestBodyCompiler.INJECTED_ARRAY_MARKER`; the compiler strips it before
 * the expression is evaluated.
 */
export const ARRAY_INJECTION_MARKER = "x-oneteg-injected-array";

/**
 * Token type of the marker, so that text a user types (a plain `input` token) is never mistaken
 * for it. Must match `SwaggerRequestBodyCompiler.INJECTED_ARRAY_MARKER_TYPE`.
 */
export const ARRAY_INJECTION_MARKER_TYPE = "injected-array";

export type InjectableReferenceType =
  | "output"
  | "metaoutput"
  | "user-defined-variable";

/** Must match `INJECTABLE_REFERENCE_TYPES` in `SwaggerRequestBodyCompiler`. */
const INJECTABLE_REFERENCE_TYPES: string[] = [
  "output",
  "metaoutput",
  "user-defined-variable",
];

export interface InjectedReference {
  name: string;
  type: string;
  origId?: string;
}

export interface InjectionEvent {
  name: string;
  path?: string;
  stepIndex?: number;
  stepNumber?: number;
  origId?: string;
}

function toTokenName(type: InjectableReferenceType, event: InjectionEvent) {
  if (type === "metaoutput") {
    const parts = (event.name ?? "").split("/");
    return `/metaoutput/#ix-${event.stepIndex}@${parts[parts.length - 1]}`;
  }
  if (type === "user-defined-variable") {
    return `/userDefinedVariables${event.name}`;
  }
  return event.name;
}

/** Builds the single array entry that injects the whole referenced value into the array. */
export function buildInjectedArrayItem(
  type: InjectableReferenceType,
  event: InjectionEvent
): string {
  const reference: Record<string, string> = {
    name: toTokenName(type, event),
    type,
    function: "",
  };
  if (event.origId) {
    reference.origId = event.origId;
  }
  return JSON.stringify([
    reference,
    {
      name: ARRAY_INJECTION_MARKER,
      type: ARRAY_INJECTION_MARKER_TYPE,
      function: "",
    },
  ]);
}

/** Returns the injected reference when `data` is a whole-array injection, otherwise null. */
export function parseInjectedArray(data: unknown): InjectedReference | null {
  if (
    !Array.isArray(data) ||
    data.length !== 1 ||
    typeof data[0] !== "string"
  ) {
    return null;
  }
  try {
    const tokens = JSON.parse(data[0]);
    if (!Array.isArray(tokens)) {
      return null;
    }
    const isMarker = (token: any) =>
      token?.name === ARRAY_INJECTION_MARKER &&
      token.type === ARRAY_INJECTION_MARKER_TYPE;
    // Injections saved before the marker had its own type: exactly a reference (of a type that can
    // be injected), then a plain input token of that name.
    const isSavedMarker =
      tokens.length === 2 &&
      INJECTABLE_REFERENCE_TYPES.includes(tokens[0]?.type) &&
      tokens[1]?.name === ARRAY_INJECTION_MARKER &&
      tokens[1].type === "input";
    if (!tokens.some(isMarker) && !isSavedMarker) {
      return null;
    }
    // Text typed by a user with the marker's name next to a real marker is not an injection.
    const rest = tokens.filter(
      (token, index) => !isMarker(token) && !(isSavedMarker && index === 1)
    );
    if (rest.some((token) => token?.name === ARRAY_INJECTION_MARKER)) {
      return null;
    }
    return rest.find((token) => token?.name) ?? null;
  } catch (e) {
    return null;
  }
}

/**
 * Label for the injected reference, in the same raw form the injected-object chip of a request
 * body shows (e.g. `/3/#amd.Content-Type`). `stepNumbers` maps the step index in the reference to
 * the number shown to the user; a step without a number keeps its index.
 */
export function getInjectedArrayLabel(
  reference: InjectedReference,
  stepNumbers: Map<number, number>
): string {
  const { name, type } = reference;

  if (type === "user-defined-variable") {
    return name.replace("/userDefinedVariables", "");
  }
  const outputMatch = name.match(/^\/(\d+)(\/#.*)$/);
  if (type === "output" && outputMatch) {
    const index = Number(outputMatch[1]);
    return `/${stepNumbers.get(index) ?? index}${outputMatch[2]}`;
  }
  const metaMatch = name.match(/^\/metaoutput\/#ix-(\d+)@(.*)$/);
  if (type === "metaoutput" && metaMatch) {
    const index = Number(metaMatch[1]);
    const path = metaMatch[2].replace(/^#/, "").replaceAll("@#", "/");
    return `${stepNumbers.get(index) ?? index}./${path}`;
  }
  return name;
}
